export type TrackerPickerDevice = {
  id: number;
  name?: string | null;
  serial?: string;
  serial_number?: string;
  imei?: string;
  status?: string;
  subscription_status?: string;
};

export type TrackerPickerValue = {
  value: number;
  label: string;
};

export function trackerPickerDeviceName(device: TrackerPickerDevice): string {
  return device.name != null && String(device.name).trim() !== ''
    ? String(device.name)
    : 'Unnamed tracker';
}

export function trackerPickerDeviceSerial(device: TrackerPickerDevice): string {
  return device.serial ?? device.serial_number ?? '—';
}

export function trackerPickerDeviceImei(device: TrackerPickerDevice): string {
  return device.imei ?? '—';
}

export function trackerPickerDeviceId(device: TrackerPickerDevice): string {
  return String(device.id ?? '');
}

export function isAssignableTrackerDevice(device: TrackerPickerDevice): boolean {
  const status = String(device?.status ?? '').toLowerCase();
  if (status !== 'active' && status !== 'provisioned') return false;
  const sub = device?.subscription_status;
  if (sub != null && String(sub).toLowerCase() !== 'active') return false;
  return true;
}

export function filterTrackerPickerDevices(
  devices: readonly TrackerPickerDevice[],
  query: string,
): TrackerPickerDevice[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...devices];
  return devices.filter((d) => {
    const name = String(d.name ?? '').toLowerCase();
    const serial = String(d.serial ?? d.serial_number ?? '').toLowerCase();
    const imei = String(d.imei ?? '').toLowerCase();
    const idStr = String(d.id ?? '').toLowerCase();
    return (
      name.includes(q) ||
      serial.includes(q) ||
      imei.includes(q) ||
      idStr.includes(q)
    );
  });
}

/**
 * Scroll target when intentionally revealing selection (open / devices loaded).
 * Not used on click/toggle — selection must never move the list (ENG-2810).
 */
export function firstTrackerPickerScrollTargetId(
  selected: readonly TrackerPickerValue[],
): number | null {
  const first = selected[0];
  return first?.value ?? null;
}

/**
 * TrackerPicker list scroll policy (ENG-2810):
 * - Click / toggle selection → never scroll (selection is not an input here).
 * - Keyboard arrows → scroll to highlighted row (handled in the component).
 * - Search/filter change → top of filtered list.
 * - Open / load with no search → first selected row (if any).
 */
export type TrackerPickerListScrollAction = 'none' | 'top' | 'selected';

export function resolveTrackerPickerListScrollAction(input: {
  loading: boolean;
  active: boolean;
  filteredCount: number;
  searchTrim: string;
}): TrackerPickerListScrollAction {
  if (input.loading || !input.active || input.filteredCount === 0) {
    return 'none';
  }
  if (input.searchTrim !== '') {
    return 'top';
  }
  return 'selected';
}

export function findTrackerPickerDeviceIndex(
  devices: readonly TrackerPickerDevice[],
  deviceId: number | null | undefined,
): number {
  if (deviceId == null) return -1;
  return devices.findIndex((device) => device.id === deviceId);
}

export function nextTrackerPickerHighlightIndex(
  current: number,
  direction: 'up' | 'down',
  itemCount: number,
): number {
  if (itemCount <= 0) return -1;
  if (current < 0) {
    return direction === 'down' ? 0 : itemCount - 1;
  }
  if (direction === 'down') {
    return Math.min(current + 1, itemCount - 1);
  }
  return Math.max(current - 1, 0);
}

export function trackerPickerValueFromDevice(
  device: TrackerPickerDevice,
): TrackerPickerValue {
  return {
    value: device.id,
    label: trackerPickerDeviceName(device),
  };
}
